import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FormData } from '../CareersApplicationForm';

interface TechnicalInfoStepProps {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
}

const technicalSkills = [
  'React', 'Vue.js', 'Angular', 'Node.js', 'Python', 'Java', 'C#', 'Go', 'Rust',
  'TypeScript', 'JavaScript', 'PHP', 'Ruby', 'Swift', 'Kotlin', 'Flutter',
  'React Native', 'Docker', 'Kubernetes', 'AWS', 'Azure', 'GCP', 'MongoDB',
  'PostgreSQL', 'MySQL', 'GraphQL', 'REST APIs', 'Microservices', 'DevOps',
  'CI/CD', 'Git', 'Jenkins', 'TensorFlow', 'PyTorch', 'Machine Learning',
  'Data Science', 'Blockchain'
];

const programmingLanguages = [
  'JavaScript', 'TypeScript', 'Python', 'Java', 'C#', 'Go', 'Rust', 'PHP',
  'Ruby', 'Swift', 'Kotlin', 'Dart', 'C++', 'C', 'Scala', 'Clojure',
  'Elixir', 'Haskell'
];

const TechnicalInfoStep: React.FC<TechnicalInfoStepProps> = ({ formData, updateFormData }) => {
  const isTechnicalRole = formData.positionType.includes('developer') ||
                         formData.positionType.includes('engineer') ||
                         formData.positionType === 'data-engineer' ||
                         formData.positionType === 'devops-engineer' ||
                         formData.positionType === 'ai-engineer';

  const handleSkillToggle = (skill: string, checked: boolean) => {
    let updatedSkills = formData.technicalSkills || [];
    if (checked) {
      updatedSkills = [...updatedSkills, skill];
    } else {
      updatedSkills = updatedSkills.filter(s => s !== skill);
    }
    updateFormData({ technicalSkills: updatedSkills });
  };

  const handleLanguageToggle = (language: string, checked: boolean) => {
    let updatedLanguages = formData.preferredLanguages || [];
    if (checked) {
      updatedLanguages = [...updatedLanguages, language];
    } else {
      updatedLanguages = updatedLanguages.filter(l => l !== language);
    }
    updateFormData({ preferredLanguages: updatedLanguages });
  };

  if (!isTechnicalRole) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-4">Professional Links</h3>
          <p className="text-sm text-gray-600 mb-6">Please provide your professional profiles.</p>
        </div>

        <div>
          <Label htmlFor="linkedinUrl">LinkedIn Profile URL *</Label>
          <Input
            id="linkedinUrl"
            value={formData.linkedinUrl}
            onChange={(e) => updateFormData({ linkedinUrl: e.target.value })}
            placeholder="https://linkedin.com/in/yourprofile"
          />
        </div>

        <div>
          <Label htmlFor="portfolioUrl">Portfolio/Website URL</Label>
          <Input
            id="portfolioUrl"
            value={formData.portfolioUrl}
            onChange={(e) => updateFormData({ portfolioUrl: e.target.value })}
            placeholder="https://yourportfolio.com"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Technical Information</h3>
        <p className="text-sm text-gray-600 mb-6">Tell us about your technical background and skills.</p>
      </div>

      <div>
        <Label htmlFor="githubUrl">GitHub Profile URL *</Label>
        <Input
          id="githubUrl"
          value={formData.githubUrl}
          onChange={(e) => updateFormData({ githubUrl: e.target.value })}
          placeholder="https://github.com/yourusername"
        />
      </div>

      <div>
        <Label htmlFor="linkedinUrl">LinkedIn Profile URL *</Label>
        <Input
          id="linkedinUrl"
          value={formData.linkedinUrl}
          onChange={(e) => updateFormData({ linkedinUrl: e.target.value })}
          placeholder="https://linkedin.com/in/yourprofile"
        />
      </div>

      <div>
        <Label htmlFor="portfolioUrl">Portfolio/Website URL</Label>
        <Input
          id="portfolioUrl"
          value={formData.portfolioUrl}
          onChange={(e) => updateFormData({ portfolioUrl: e.target.value })}
          placeholder="https://yourportfolio.com"
        />
      </div>

      <div>
        <Label className="text-base font-medium">Technical Skills * (Select all that apply)</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 max-h-60 overflow-y-auto">
          {technicalSkills.map((skill) => (
            <div key={skill} className="flex items-center space-x-2">
              <Checkbox
                id={skill}
                checked={formData.technicalSkills?.includes(skill) || false}
                onCheckedChange={(checked) => handleSkillToggle(skill, !!checked)}
              />
              <Label htmlFor={skill} className="text-sm font-normal">{skill}</Label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-base font-medium">Preferred Programming Languages * (Select all that apply)</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
          {programmingLanguages.map((language) => (
            <div key={language} className="flex items-center space-x-2">
              <Checkbox
                id={language}
                checked={formData.preferredLanguages?.includes(language) || false}
                onCheckedChange={(checked) => handleLanguageToggle(language, !!checked)}
              />
              <Label htmlFor={language} className="text-sm font-normal">{language}</Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TechnicalInfoStep;